/**
 * useNodeUpdate Hook
 *
 * Provides centralized update methods for attack path nodes.
 * Eliminates duplicated setNodes handlers across node components.
 *
 * Features:
 * - Type-safe update methods for each node type
 * - Uses Provider's updateNode for centralized state management
 * - Prevents direct setNodes calls in node components
 */

import { useCallback } from 'react';

import { useAttackState } from '@/sections/attacks/attackPaths/context';
import type { TechniqueStatus } from '@/sections/attacks/attackPaths/types';

/**
 * Return type for useNodeUpdate hook
 */
interface UseNodeUpdateReturn {
  /** Update asset node data */
  updateAsset: (
    assetId: string,
    isCustom: boolean,
    iconOverride?: string,
    displayName?: string,
    metadata?: string
  ) => void;
  /** Update technique node data */
  updateTechnique: (
    techniqueId: string,
    name?: string,
    status?: TechniqueStatus
  ) => void;
  /** Update vulnerability node data */
  updateVulnerability: (
    key: string,
    isCustom: boolean,
    name?: string,
    priority?: number
  ) => void;
}

/**
 * Hook for updating attack path node data.
 *
 * Provides type-safe methods for updating each node type,
 * centralizing the update logic that was previously duplicated
 * across AssetNode, TechniqueNode, and VulnerabilityNode components.
 *
 * @param nodeId - The ID of the node to update
 * @returns Object with update methods for each node type
 *
 * @example
 * ```tsx
 * const { updateAsset, updateTechnique, updateVulnerability } = useNodeUpdate(id);
 *
 * // Update asset node
 * updateAsset('new-asset-key', false, 'Server');
 *
 * // Update technique node
 * updateTechnique('T1059', 'Command and Scripting Interpreter', 'detected');
 *
 * // Update vulnerability node
 * updateVulnerability('#risk#CVE-2024-1234', 'Critical RCE', false, 0);
 * ```
 */
export function useNodeUpdate(nodeId: string): UseNodeUpdateReturn {
  const { updateNode } = useAttackState();

  /**
   * Update asset node with new data
   */
  const updateAsset = useCallback(
    (
      assetId: string,
      isCustom: boolean,
      iconOverride?: string,
      displayName?: string,
      metadata?: string
    ) => {
      updateNode(nodeId, {
        id: assetId,
        type: isCustom ? 'nonChariot' : undefined,
        iconOverride,
        displayName,
        metadata,
      });
    },
    [nodeId, updateNode]
  );

  /**
   * Update technique node with new data
   */
  const updateTechnique = useCallback(
    (techniqueId: string, name?: string, status?: TechniqueStatus) => {
      updateNode(nodeId, {
        id: techniqueId,
        name: name || '',
        status,
      });
    },
    [nodeId, updateNode]
  );

  /**
   * Update vulnerability node with new data
   */
  const updateVulnerability = useCallback(
    (key: string, isCustom: boolean, name?: string, priority?: number) => {
      updateNode(nodeId, {
        key,
        name,
        type: isCustom ? 'nonChariot' : undefined,
        priority,
      });
    },
    [nodeId, updateNode]
  );

  return { updateAsset, updateTechnique, updateVulnerability };
}
